<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { NxButton } from '../Button';
import { NxDialog } from '../Dialog';
import { i18nInjectionKey } from '../internal';
import NxTableFilterWizardGroup from './NxTableFilterWizardGroup.vue';
import {
  cloneFilterGroup,
  countGroupConditions,
  normalizeFilterGroup,
  type NxTableFilterColumnMeta,
  type NxTableFilterGroup,
} from './filterModel';

/**
 * Filter wizard: builds an arbitrarily nested and/or condition tree over all
 * filterable columns. Apply sets the table's advanced filter; save stores the
 * tree as part of a named filter set.
 */
const isOpen = defineModel<boolean>('open', { required: true });

const props = defineProps<{
  columns: NxTableFilterColumnMeta[];
  /** Currently active advanced filter (edited in place on open). */
  initial: NxTableFilterGroup | null;
}>();

const emit = defineEmits<{
  apply: [tree: NxTableFilterGroup | null];
  save: [tree: NxTableFilterGroup | null];
}>();

const i18n = inject(i18nInjectionKey)!;

const draft = ref<NxTableFilterGroup>({ modifier: 'and', children: [] });

watch(isOpen, (open) => {
  if (!open) return;
  draft.value = props.initial ? cloneFilterGroup(props.initial) : { modifier: 'and', children: [] };
});

const conditionCount = computed(() => countGroupConditions(draft.value));

function apply(close: () => void) {
  emit('apply', normalizeFilterGroup(draft.value));
  close();
}

function saveAndApply(close: () => void) {
  emit('save', normalizeFilterGroup(draft.value));
  close();
}
</script>

<template>
  <NxDialog
    v-model:open="isOpen"
    :title="i18n.t('common.table.filter.wizard.title')"
    :description="i18n.t('common.table.filter.wizard.description')"
    size="xwide"
    fill
  >
    <template #content>
      <div class="wizard-body">
        <NxTableFilterWizardGroup v-model="draft" :columns="props.columns" />
        <p class="wizard-summary">
          {{ i18n.t('common.table.filter.wizard.summary', { count: conditionCount }) }}
        </p>
      </div>
    </template>
    <template #buttons="{ close }">
      <NxButton @click="() => apply(close)">
        {{ i18n.t('common.table.filter.apply') }}
      </NxButton>
      <NxButton
        variant="outline"
        :disabled="conditionCount === 0"
        @click="() => saveAndApply(close)"
      >
        {{ i18n.t('common.table.filter.wizard.save') }}
      </NxButton>
      <NxButton variant="outline" @click="close">
        {{ i18n.t('common.table.filter.wizard.cancel') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.wizard-body {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  margin-top: var(--nxt-space-2xs);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}

.wizard-summary {
  margin: 0;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}
</style>
